import {useMemo, useState} from "react";
import {PhoneHQAvatar} from 'ch3-ui-lib';
import {
    EnsChannel,
    EnsNotification,
    EnsRecipient,
    EnsRecipientStatus,
} from "../../../store/features/ens/types";
import {useFetchUsersQuery, UserElement} from "../../../store/features/Users/userService";
import {StatusBadge} from "../components/StatusBadge";
import {getRecipientConfirmationChannelLabel} from "./recipientDisplayUtils";

interface Props {
    recipients: EnsRecipient[];
    notifications: EnsNotification[];
}

type FilterKey = 'ALL' | 'PENDING' | 'CONFIRMED' | 'FAILED';

const FILTERS: {key: FilterKey; label: string; match: (s: EnsRecipientStatus) => boolean}[] = [
    {key: 'ALL', label: 'All', match: () => true},
    {key: 'PENDING', label: 'Pending', match: (s) => s === 'PENDING'},
    {key: 'CONFIRMED', label: 'Confirmed', match: (s) => s === 'CONFIRMED'},
    {key: 'FAILED', label: 'Failed', match: (s) => s === 'FAILED'},
];

const CHANNEL_LABEL: Record<EnsChannel, string> = {
    PUSH: 'Push',
    INTERNAL_CALL: 'Internal call',
    SMS: 'SMS',
    PSTN: 'Phone call',
    WHATSAPP: 'WhatsApp',
};

const formatTime = (iso: string | null | undefined): string => {
    if (!iso) return '';
    try {
        const date = new Date(iso);
        const today = new Date();
        const isToday = date.getFullYear() === today.getFullYear()
            && date.getMonth() === today.getMonth()
            && date.getDate() === today.getDate();
        if (isToday) {
            return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
        }
        return date.toLocaleDateString([], {day: '2-digit', month: '2-digit'}) + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
    } catch {
        return '';
    }
};

export const RecipientsTab = ({recipients, notifications}: Props) => {
    const {data: users = []} = useFetchUsersQuery({});
    const [activeFilter, setActiveFilter] = useState<FilterKey>('ALL');

    const userMap = useMemo(() => {
        const map: Record<number, UserElement> = {};
        users.forEach((u) => {
            map[u.id] = u;
        });
        return map;
    }, [users]);

    const latestNotifByRecipient = useMemo(() => {
        const map: Record<number, EnsNotification> = {};
        notifications.forEach((n) => {
            const existing = map[n.recipientId];
            if (!existing || n.levelSequence > existing.levelSequence) {
                map[n.recipientId] = n;
            }
        });
        return map;
    }, [notifications]);

    const counts = useMemo(() => {
        const c: Record<FilterKey, number> = {ALL: recipients.length, PENDING: 0, CONFIRMED: 0, FAILED: 0};
        recipients.forEach((r) => {
            if (r.status === 'PENDING') c.PENDING++;
            else if (r.status === 'CONFIRMED') c.CONFIRMED++;
            else if (r.status === 'FAILED') c.FAILED++;
        });
        return c;
    }, [recipients]);

    const filtered = useMemo(() => {
        const filter = FILTERS.find((f) => f.key === activeFilter)!;
        return recipients.filter((r) => filter.match(r.status));
    }, [recipients, activeFilter]);

    if (recipients.length === 0) {
        return (
            <div className="px-5 py-10 text-center text-sm text-ink-muted">
                No recipients.
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2 px-4 py-3">
            <div className="flex flex-wrap gap-1">
                {FILTERS.map((f) => (
                    <button
                        key={f.key}
                        type="button"
                        onClick={() => setActiveFilter(f.key)}
                        className={`inline-flex h-[26px] items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold transition-colors ${
                            activeFilter === f.key
                                ? 'bg-inverse-surface text-white'
                                : 'bg-surface-muted text-ink-secondary hover:bg-surface-muted'
                        }`}
                    >
                        {f.label}
                        <span className={activeFilter === f.key ? 'text-white/70' : 'text-ink-muted'}>
                            {counts[f.key]}
                        </span>
                    </button>
                ))}
            </div>

            {filtered.map((recipient) => {
                const user = userMap[recipient.userId];
                const name = user ? `${user.firstName} ${user.lastName}` : `#${recipient.userId}`;
                const notif = latestNotifByRecipient[recipient.id];
                const confirmationChannel = getRecipientConfirmationChannelLabel(recipient);

                const subtitleParts: string[] = [];
                if (confirmationChannel) {
                    subtitleParts.push(`Confirmed via ${confirmationChannel}`);
                } else if (notif) {
                    subtitleParts.push(`L${notif.levelSequence}`);
                    subtitleParts.push(CHANNEL_LABEL[notif.channel] ?? notif.channel);
                } else if (recipient.currentLevelSequence) {
                    subtitleParts.push(`L${recipient.currentLevelSequence}`);
                }
                if (recipient.confirmedAt) {
                    subtitleParts.push(formatTime(recipient.confirmedAt));
                } else if (notif?.scheduledAt) {
                    subtitleParts.push(formatTime(notif.scheduledAt));
                }

                return (
                    <div
                        key={recipient.id}
                        className="flex items-center gap-2.5 rounded-lg border border-line bg-surface-muted px-2.5 py-2"
                    >
                        <PhoneHQAvatar src={user?.avatarUrl ?? undefined} name={name} size="32px"/>
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-xs font-semibold text-ink">{name}</div>
                            {subtitleParts.length > 0 && (
                                <div className="text-[10px] text-ink-muted">{subtitleParts.join(' · ')}</div>
                            )}
                        </div>
                        <StatusBadge status={recipient.status} variant="dot"/>
                    </div>
                );
            })}
            {filtered.length === 0 && (
                <div className="py-6 text-center text-sm text-ink-muted">
                    No recipients match this filter.
                </div>
            )}
        </div>
    );
};
